import React, { useState } from 'react';
import { Button, CircularProgress, useTheme } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { DOC_TYPE_REQUIRED } from '@europrocurement/l2d-domain';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { useDossierDocuments } from '../../../hooks';
import { useDocumentContext, useSelectedDossierContext } from '../../../providers';

const DownloadRequiredDocumentsButton: React.FunctionComponent = function () {
    const { dossier } = useSelectedDossierContext();
    const { downloadFilesAsArchive } = useDossierDocuments();
    const { palette } = useTheme();
    const [processing, setProcessing] = useState<boolean>(false);

    const { getPrescriberDocuments, getPrescriberAdditionalDocuments } = useDocumentContext();

    const countUploaded = [
        ...getPrescriberDocuments(),
        ...getPrescriberAdditionalDocuments(),
    ].reduce((accumulator, doc) => (doc.fichierId ? accumulator + 1 : accumulator), 0);

    if (!countUploaded) {
        return null;
    }

    const handleDownloadFiles = async () => {
        setProcessing(true);

        try {
            await downloadFilesAsArchive(`${dossier.numero}.zip`, dossier.id as number, [
                DOC_TYPE_REQUIRED,
            ]);
        } catch (e) {
            console.error(e);

            enqueueSnackbar('Une erreur est survenue lors du téléchargement de vos documents', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const buttonSx = {
        display: 'flex',
        columnGap: '10px',
        paddingX: '1em',
        color: palette.primary.contrastText,
        backgroundColor: palette.primary.dark,
        ':hover': {
            backgroundColor: palette.primary.main,
        },
        ':disabled': {
            backgroundColor: palette.grey[100],
        },
    };

    const circularSx = {
        color: palette.grey[400],
    };

    return (
        <Button
            onClick={handleDownloadFiles}
            sx={buttonSx}
            disabled={processing}
        >
            {processing && (
                <CircularProgress
                    sx={circularSx}
                    size="16px"
                />
            )}
            {!processing && <FaOptionIcon icon={['fas', 'file-download']} />}
            Télécharger les documents
        </Button>
    );
};

export default DownloadRequiredDocumentsButton;
